import { useEffect, useState } from "react";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { useNavigate } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { useActiveStore } from "../../context/ActiveStoreContext";
import { useEnhancedPermissions } from "../../hooks/useEnhancedPermissions";
import {
  Connectintegrations,
  Profile,
  Support,
  SubscriptionsIcon,
  SettingIconDark,
  SettingIconLight,
  BookDemoIcondark,
  BookDemoIconlight,
  ReportIcon,
} from "../../icons";
import BookDemoModal from "../../pages/Demo/BookDemoModal";

export default function UserDropdown({
  disableNavigation = false,
  availableCredits = null,
}: {
  disableNavigation?: boolean;
  availableCredits?: number | null;
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showBookDemoPopup, setShowBookDemoPopup] = useState(false);
  const [showDemoLinkPopup, setShowDemoLinkPopup] = useState(false);
  const [showProductTourMenu, setShowProductTourMenu] = useState(false);

  const { user, logout, stores, currentShop, currentSubscription } = useAuth();
  const { activeStore } = useActiveStore();
  const { checkPermission } = useEnhancedPermissions();

  // Check if user is owner or admin - if so, show all menu items
  const isOwnerOrAdmin =
    activeStore?.user_role === "owner" || activeStore?.user_role === "admin";
  const isMember = activeStore?.user_role === "member";

  const trialDaysRemaining = currentSubscription?.trial_days_remaining;
  // const showTrialDaysBanner =
  //   typeof trialDaysRemaining === "number" &&
  //   trialDaysRemaining >= 0 &&
  //   trialDaysRemaining <= 7;
  // const trialDaysRemaining = currentSubscription?.trial_days_remaining;
  

  // const showTrialDaysBanner =
  //   typeof trialDaysRemaining === "number" &&
  //   trialDaysRemaining >= 0 &&
  //   trialDaysRemaining <= 7;

  // const isTrialEnded = trialDaysRemaining === 0;
  const isPaidSubscription =
    Number((currentSubscription as any)?.price ?? 0) > 0;

  const showTrialDaysBanner =
    !isPaidSubscription &&
    typeof trialDaysRemaining === "number" &&
    trialDaysRemaining >= 0 &&
    trialDaysRemaining <= 7;

  const isTrialEnded = trialDaysRemaining === 0;

  const navigate = useNavigate();

  const hasConnectedStore = stores.some((store) => store.connected);
  const currentStore =
    stores.find((store) => store.shop === currentShop) || null;
  const isCurrentStoreConnected = currentStore?.connected === true;

  const navDisabled =
    disableNavigation ||
    !stores.length ||
    !hasConnectedStore ||
    !isCurrentStoreConnected;

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
    setShowProductTourMenu(false);
  }

  // Mobile only: freeze background while user dropdown is open
  useEffect(() => {
    if (!isOpen || window.innerWidth >= 768) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!showDemoLinkPopup) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showDemoLinkPopup]);

  const handleLogout = async () => {
    // Store current page URL for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    // Clear trial modal session flag so it shows again on next login
    try { sessionStorage.removeItem("trial_modal_shown"); } catch { /* ignore */ }
    await logout(currentPath);
    navigate("/signin");
  };

  const handleOpenReports = () => {
    closeDropdown();
    navigate("/reports");
  };
  return (
    <div className="relative mr-[8px]">
      <button
        onClick={toggleDropdown}
        className="flex items-center text-gray-700 dropdown-toggle dark:text-gray-100"
      >
        <span className="block mr-1 font-medium text-theme-sm">
          <span className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full hover:text-dark-900 h-9 w-9 hover:bg-gray-100 hover:text-gray-700 dark:border-[#FFFFFF0F] dark:bg-[var(--card)] dark:text-gray-100 dark:hover:bg-gray-800 dark:hover:text-white">
            {user?.name
              ?.split(" ")
              .map((name) => name.charAt(0).toUpperCase())
              .join("") || "User"}
          </span>
        </span>

        <svg
          className={`stroke-gray-500 dark:stroke-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          width="18"
          height="20"
          viewBox="0 0 18 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.3125 8.65625L9 13.3437L13.6875 8.65625"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute right-0 flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-[#FFFFFF0F] dark:bg-[var(--card)]"
      >
        <div>
          <span className="block font-medium text-gray-700 text-theme-sm dark:text-gray-200 -mb-3">
            {user?.name || "User"}
          </span>
          {/* <span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
            {user?.email || "user@example.com"}
          </span> */}
        </div>

        <ul className="flex flex-col gap-1 pt-4 pb-3 border-b border-gray-200 dark:border-[#FFFFFF0F] -ml-1.5">
          <li className="block lg:hidden">
            <hr className="my-1 border-gray-200 dark:border-[#FFFFFF0F]" />
            <div className="flex items-center justify-between gap-2 px-0.5">
              <div className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[var(--accent)]/30 bg-[var(--accent)]/10 px-2.5 py-1.5 text-[12px] font-semibold text-[var(--accent)] shrink-0 sm:gap-2 sm:px-3.5 sm:text-[13px] font-[Inter,sans-serif]">
                <span className="font-bold opacity-80">+ Credits:</span>
                <span className="font-bold">
                  {availableCredits !== null ? availableCredits : 0}
                </span>
              </div>
            </div>
            <hr className="my-1 border-gray-200 dark:border-[#FFFFFF0F]" />
            {/* {showTrialDaysBanner ? (
              <>
                <div className="flex cursor-pointer items-center gap-1.5 px-2 py-1.5 text-[12px] font-semibold text-red-500 shrink-0 sm:gap-2 sm:px-3.5 sm:text-[13px] font-[Inter,sans-serif] animate-pulse">
                 Your free trial expires in {trialDaysRemaining} days
                </div>
            <hr className="my-1 border-gray-200 dark:border-[#FFFFFF0F]" /></>
              ) : null} */}
            {showTrialDaysBanner ? (
              <>
                <div
                  className={`flex flex-col items-start px-2 py-1.5 text-[12px] font-semibold sm:px-3.5 sm:text-[13px] font-[Inter,sans-serif] ${
                    isTrialEnded
                      ? "text-red-600 dark:text-red-400"
                      : "text-[#2d9d97] animate-pulse dark:text-[#2d9d97]"
                  }`}
                >
                  <span>
                    {isTrialEnded
                      ? "Your trial period has ended."
                      : `Your free trial expires in ${trialDaysRemaining} day${
                          trialDaysRemaining !== 1 ? "s" : ""
                        }.`}
                  </span>

                  <span
                    onClick={() => navigate("/billing")}
                    className="mt-0.5 cursor-pointer underline hover:opacity-80"
                  >
                    Upgrade now
                  </span>
                </div>

                <hr className="my-1 border-gray-200 dark:border-[#FFFFFF0F]" />
              </>
            ) : null}
          </li>
          {/* Profile is always allowed */}
          <li>
            <DropdownItem
              onItemClick={() => {
                closeDropdown();
                navigate("/profile");
              }}
              tag="button"
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 w-full text-left "
            >
              <Profile className="h-5 w-5 text-blue-500 -ml-2" />
              Profile
            </DropdownItem>
          </li>

          {/* Only show other navigation items when navigation is enabled */}
          {!navDisabled && (
            <>
              {/* Billing - Redirect members to profile */}
              {(isOwnerOrAdmin || checkPermission("billing_access")) && (
                <li>
                  <DropdownItem
                    onItemClick={() => {
                      closeDropdown();
                      if (isMember) {
                        navigate("/profile");
                      } else {
                        navigate("/billing");
                      }
                    }}
                    tag="button"
                    className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                  >
                    <SubscriptionsIcon className="h-5 w-5 text-blue-500 -ml-2" />
                    Billing
                  </DropdownItem>
                </li>
              )}
              {/* Connect integrations - Redirect members to profile */}
              {isCurrentStoreConnected &&
                (isOwnerOrAdmin || checkPermission("integrations_access")) && (
                  <li>
                    <DropdownItem
                      onItemClick={() => {
                        closeDropdown();
                        if (isMember) {
                          navigate("/profile");
                        } else {
                          navigate("/integrations");
                        }
                      }}
                      tag="a"
                      to="/integrations"
                      className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                    >
                      <Connectintegrations className="h-5 w-5 text-blue-500 -ml-2" />
                      Integrations
                    </DropdownItem>
                  </li>
                )}
              {/* Settings - Only check permission for members */}
              {(isOwnerOrAdmin ||
                !isMember ||
                checkPermission("settings_access")) && (
                <li>
                  <DropdownItem
                    onItemClick={() => {
                      closeDropdown();
                      navigate("/settings");
                    }}
                    tag="button"
                    className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 w-full text-left"
                  >
                    <span className="inline dark:hidden">
                      <SettingIconDark className="h-5 w-5 text-grey-500 -ml-2" />
                    </span>
                    <span className="hidden dark:inline">
                      <SettingIconLight className="h-5 w-5 text-grey-500 -ml-2" />
                    </span>
                    Settings
                  </DropdownItem>
                </li>
              )}
            </>
          )}

          {/* Product Tour */}
          <li>
            <div className="w-full">
              <button
                onClick={() => setShowProductTourMenu(!showProductTourMenu)}
                className="flex items-center justify-between w-full gap-3 px-4 py-2 font-medium text-gray-700 rounded-lg text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
              >
                <div className="flex items-center gap-3">
                  <span className="inline dark:hidden">
                    <BookDemoIcondark className="h-5 w-5 text-[#A3A3A3] -ml-2" />
                  </span>
                  <span className="hidden dark:inline">
                    <BookDemoIconlight className="h-5 w-5 text-[#A3A3A3] -ml-2" />
                  </span>
                  Product Tour
                </div>

                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    showProductTourMenu ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showProductTourMenu && (
                <div className="ml-7 mt-1.5 flex flex-col gap-1 pl-2 dark:border-[#FFFFFF14]">
                  {/* Book A Demo */}
                  <button
                    onClick={() => {
                      closeDropdown();
                      setShowBookDemoPopup(true);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-left rounded-lg text-theme-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    <span className="text-[10px]">•</span>
                    Book A Demo
                  </button>

                  {/* Watch Demo */}
                  <button
                    onClick={() => {
                      closeDropdown();
                      setShowDemoLinkPopup(true);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-left rounded-lg text-theme-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    <span className="text-[10px]">•</span>
                    Watch Demo
                  </button>
                </div>
              )}
            </div>
          </li>

          <li>
            <DropdownItem
              onItemClick={() => {
                closeDropdown();
              }}
              tag="a"
              to="/support"
              className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
            >
              <Support className="h-5 w-5 text-blue-500 -ml-2" />
              Support
            </DropdownItem>
          </li>
          {/* Reports - Always visible when store is connected */}
          {isCurrentStoreConnected && (
            <li>
              <DropdownItem
                onItemClick={handleOpenReports}
                tag="button"
                className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 w-full text-left"
              >
              <ReportIcon className="h-5 w-5 text-blue-500 -ml-2" />
                Reports
              </DropdownItem>
            </li>
          )}
        </ul>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 mt-3 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 -ml-1.5"
        >
          <div className="-ml-2.5">
            <svg
              className="fill-gray-500 group-hover:fill-gray-700 dark:group-hover:fill-gray-300"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.1007 19.247C14.6865 19.247 14.3507 18.9112 14.3507 18.497L14.3507 14.245H12.8507V18.497C12.8507 19.7396 13.8581 20.747 15.1007 20.747H18.5007C19.7434 20.747 20.7507 19.7396 20.7507 18.497L20.7507 5.49609C20.7507 4.25345 19.7433 3.24609 18.5007 3.24609H15.1007C13.8581 3.24609 12.8507 4.25345 12.8507 5.49609V9.74501L14.3507 9.74501V5.49609C14.3507 5.08188 14.6865 4.74609 15.1007 4.74609L18.5007 4.74609C18.9149 4.74609 19.2507 5.08188 19.2507 5.49609L19.2507 18.497C19.2507 18.9112 18.9149 19.247 18.5007 19.247H15.1007ZM3.25073 11.9984C3.25073 12.2144 3.34204 12.4091 3.48817 12.546L8.09483 17.1556C8.38763 17.4485 8.86251 17.4487 9.15549 17.1559C9.44848 16.8631 9.44863 16.3882 9.15583 16.0952L5.81116 12.7484L16.0007 12.7484C16.4149 12.7484 16.7507 12.4127 16.7507 11.9984C16.7507 11.5842 16.4149 11.2484 16.0007 11.2484L5.81528 11.2484L9.15585 7.90554C9.44864 7.61255 9.44847 7.13767 9.15547 6.84488C8.86248 6.55209 8.3876 6.55226 8.09481 6.84525L3.52309 11.4202C3.35673 11.5577 3.25073 11.7657 3.25073 11.9984Z"
                fill=""
              />
            </svg>
          </div>
          Logout
        </button>
      </Dropdown>

      {/* Book a Demo Modal */}
      {showBookDemoPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="w-full max-w-2xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-6rem)] min-h-[75vh] md:min-h-[70vh] rounded-lg bg-white shadow-xl px-4">
            <div className="flex items-center justify-between z-50 relative">
              <div className="flex items-center gap-4">
                <h2 className="text-xl p-4 font-bold">
                  Pick your time slot for expert assistance
                </h2>
              </div>
              <button
                onClick={() => setShowBookDemoPopup(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div>
              <iframe
                className="calendly-inline-widget w-full h-[calc(100vh-18rem)]"
                src="https://calendly.com/netsightsai/book-a-demo"
                data-url="https://calendly.com/netsightsai/book-a-demo"
              />
              <script
                type="text/javascript"
                src="https://assets.calendly.com/assets/external/widget.js"
                async
              />
            </div>
          </div>
        </div>
      )}

      <BookDemoModal
        open={showDemoLinkPopup}
        onClose={() => setShowDemoLinkPopup(false)}
      />
    </div>
  );
}
